/**
 * @component Renders a toolbar with a list of action buttons and a counter of selected items.
 *
 * @param {number} count - The total count of items. Used to show the "Items Showing : X" toolbar
 * @param {number} selected - The number of selected items.
 * @param {object[]} actions - An array of objects representing the action buttons to be displayed on the toolbar.
 *
 **/

import { createPortal } from 'react-dom';
import { Skeleton, Slide, Toolbar, Typography } from '@mui/material';
import { Box, styled } from '@mui/system';

import { screenSize } from 'Lib/utilities/screenSize';

const ListTableToolbar = ({ loading, files, count, selected, actions, sx = {} }) => {
    const { mobile } = screenSize();

    const renderActionsToolbar = (showCount = false) => (
        <ActionToolbar mobile={mobile} files={showCount}>
            <Typography
                sx={theme => ({
                    fontSize: theme.typography.pxToRem(16),
                    whiteSpace: 'nowrap',
                    mr: 4,
                    display: 'flex'
                })}
            >
                <Box component="span" mr={1}>{`Items ${showCount ? 'Showing' : 'Selected'}:`}</Box>
                {loading ? <Skeleton sx={{ ml: 1, width: '1rem' }} /> : <span style={{ fontWeight: 600 }}>{showCount ? count : selected}</span>}
            </Typography>
            {actions && !showCount && actions()}
        </ActionToolbar>
    );

    return (
        <>
            {files && !mobile && (
                <Box
                    sx={{
                        position: 'absolute',
                        height: '56px',
                        width: '100%',
                        zIndex: 1051,
                        bottom: 0,
                        ...sx
                    }}
                >
                    {renderActionsToolbar(true)}
                </Box>
            )}
            {document.getElementById('selected-table-toolbar') ? (
                createPortal(
                    <Slide
                        appear
                        mountOnEnter
                        unmountOnExit
                        timeout={300}
                        direction="up"
                        in={selected > 0}
                        sx={{ position: 'fixed', height: '80px', width: '100%', zIndex: 1052, bottom: 0 }}
                    >
                        <Box sx={{ height: '100%', ...sx }}>{renderActionsToolbar()}</Box>
                    </Slide>,
                    document.getElementById('selected-table-toolbar')
                )
            ) : (
                <Slide
                    appear
                    mountOnEnter
                    unmountOnExit
                    timeout={300}
                    direction="up"
                    in={actions && selected > 0 && !mobile}
                    sx={{ height: '56px', width: '100%', zIndex: 1052, bottom: 0, position: 'absolute' }}
                >
                    {renderActionsToolbar()}
                </Slide>
            )}
        </>
    );
};

const ActionToolbar = styled(Toolbar, {
    shouldForwardProp: prop => prop !== 'mobile' && prop !== 'files'
})(({ theme, mobile, files }) => ({
    minHeight: '56px !important',
    height: 'inherit',
    backgroundColor: files ? theme.palette.common.lightgray : theme.palette.navigation.background.selected,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderTop: mobile || files ? 'none' : '1px solid #B9D5EA',
    borderRadius: mobile ? '12px 12px 0px 0px' : '0px 0px 4px 4px',
    padding: '8px 16px !important'
}));

export default ListTableToolbar;
